/**
 * Quick view — docs/12.
 *
 * Intercepts every [data-quick-view] control on the page, fetches the panel
 * from sections/quick-view.liquid through the Section Rendering API, and
 * opens the shared <modal-dialog> in snippets/quick-view.liquid.
 *
 * ---------------------------------------------------------------------------
 * IT IS AN ENHANCEMENT OVER A WORKING LINK, NOT A REPLACEMENT FOR ONE.
 *
 * The trigger is an <a href="{product.url}"> — see
 * product-card-actions.liquid. Without this script, or before it loads, or if
 * the fetch fails, clicking it navigates to the product page, which is the
 * correct outcome. `preventDefault()` is called only once the modal is
 * actually going to open.
 *
 * That also means a middle-click or ctrl-click still opens the PDP in a new
 * tab, which is what a link is expected to do and what a <button> could not
 * offer.
 * ---------------------------------------------------------------------------
 *
 * docs/12 §15.7: the reference gates the card rail on WIDTH, so quick view is
 * unreachable on a touchscreen laptop. component-product-card.css gates on
 * `(hover: hover)` instead, which is why nothing here needs a width check.
 */
(function () {
  const SECTION_ID = 'quick-view';

  /**
   * THE PANEL'S VARIANT PICKER DID NOTHING, AND THE ELEMENT WAS NEVER DEFINED.
   * ---------------------------------------------------------------------------
   * Merchant, 2026-08-19: "the variants dont switch on quick view."
   *
   * Measured, with the modal open on Bella 3 Comparment Extra Small Handbag:
   *
   *     customElements.get('product-info')   ->  undefined
   *     picked Beige, legend read "Colour: Beige"
   *     .product-variant-id stayed           ->  55891075006537  (BLACK)
   *     price, image: unchanged
   *
   * So the panel LOOKED like it responded — `variant-selects` is defined in
   * `global.js` and rewrites the legend on its own — while the form kept the
   * variant it was rendered with. That is not a cosmetic bug. It is §1.-48's
   * card defect one surface across: the customer picks Beige, sees Beige, and
   * the cart receives Black.
   *
   * The cause is one missing file. `product-info.js` is loaded by
   * `sections/main-product.liquid` and by nothing else, so on a collection
   * page `<product-info>` is an unknown element with no behaviour. The header
   * comment in `sections/quick-view.liquid` has claimed since Step 6.14 that
   * "price, availability and media all update through product-info.js exactly
   * as they do on the PDP" — a contract that was never true and never checked.
   *
   * WHY IT IS LOADED HERE AND NOT IN THE SECTION. A `<script src>` inside the
   * fetched section would never execute: `open()` parses the response with
   * `DOMParser` and appends the node, and scripts that arrive that way are
   * inert by specification. Adding one there is the obvious fix and it does
   * nothing at all.
   *
   * WHY LAZY AND NOT GLOBAL. Loading it on every page that renders a card puts
   * ~15 KB of JS on every collection, search and home view to serve a modal
   * most visitors never open — against a jsKB budget docs/45 already watches.
   * First open pays for it; every open after is a no-op.
   *
   * ORDER DOES NOT MATTER, WHICH IS WHY THIS IS SAFE. Custom elements upgrade
   * retroactively: whenever `customElements.define('product-info', ...)` runs,
   * every matching element already in the document upgrades. So the fetch and
   * the script load can race freely — we await both only so the panel is never
   * shown in the half-second where it is inert.
   *
   * A FAILED LOAD MUST NOT BLOCK THE MODAL. `onerror` resolves rather than
   * rejects: a quick view with a static picker is worse than one with a
   * working one and far better than none at all.
   */
  let productInfoPromise;

  function loadProductInfo(src) {
    if (customElements.get('product-info')) return Promise.resolve();
    if (productInfoPromise) return productInfoPromise;
    if (!src) return Promise.resolve();

    productInfoPromise = new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = src;
      script.defer = true;
      script.addEventListener('load', resolve, { once: true });
      script.addEventListener('error', resolve, { once: true });
      document.head.appendChild(script);
    });

    return productInfoPromise;
  }

  class QuickView extends HTMLElement {
    connectedCallback() {
      this.content = this.querySelector('[data-quick-view-content]');
      if (!this.content) return;

      // connectedCallback fires AGAIN when ModalDialog re-appends the dialog
      // to document.body, and every descendant is reconnected with it. Without
      // this guard the document listener is bound twice and one click opens
      // two fetches.
      if (this.bound) return;
      this.bound = true;

      // Delegated from the document, so cards injected later — Load More at
      // Step 8.8, recently viewed at Step 10.1 — are covered without
      // re-binding anything.
      document.addEventListener('click', this.onClick.bind(this));

      /*
       * THE BAR TILES FOLLOW THE RADIOS, they do not lead them.
       *
       * Each tile in the buy bar is a `<label for>` over one of the variant
       * picker's real radio inputs, so CHANGING the option needs no script at
       * all — the browser checks the radio and Dawn's `variant-selects` takes
       * it from there. What script is needed is the other direction: showing
       * which tile is current, including when the shopper uses the full picker
       * up in the panel rather than the bar.
       *
       * CSS cannot express it. The tiles and the inputs are cousins, not
       * siblings, so there is no `:checked + label` to lean on and `:has()`
       * would need one rule per input id.
       *
       * Bound on `this` and delegated, so it survives the Section Rendering
       * API replacing the panel on every variant change — the listener is on
       * the container, which is not what gets swapped.
       */
      this.addEventListener('change', (event) => {
        const input = event.target.closest?.('input[type="radio"]');
        if (!input) return;
        const bar = this.querySelector('[data-qv-bar-options]');
        if (!bar) return;

        /* SCOPED TO ONE ROW. The bar carries a row per option now (Colour,
           Size, …), so clearing `is-selected` across the whole bar would strip
           the ring off every other row on any change — and rewriting the first
           label it found would put a size value under "Colour:". Find the tile
           that belongs to this input, then work only inside its row. */
        const tile = bar.querySelector(`[data-qv-tile][for="${CSS.escape(input.id)}"]`);
        if (!tile) return;

        const row = tile.closest('.quick-view__bar-option');
        if (!row) return;

        row.querySelectorAll('[data-qv-tile]').forEach((t) => t.classList.toggle('is-selected', t === tile));

        const label = row.querySelector('[data-qv-option-selected]');
        if (label) label.textContent = input.value;
      });

      /* FOCUS IS FORWARDED FROM THE HIDDEN RADIO ONTO ITS TILE.
       *
       * 2026-08-21: the merchant asked for the panel's own colour and size
       * picker to go, because the bar already says both. It is CLIPPED rather
       * than removed — the bar's tiles are `<label for>` pointing at those
       * radios, so deleting them would leave every tile pointing at nothing
       * (see sections/quick-view.liquid).
       *
       * A clipped radio is still focusable, and that is deliberate: a `<label>`
       * cannot take focus, so the radio group is the ONLY keyboard path to
       * choosing a colour. But its focus ring is clipped along with it, so a
       * keyboard user would be arrowing an invisible cursor through the
       * options — worse than the duplication that was removed.
       *
       * So the tile wears the ring instead. CSS cannot do this: the radio and
       * the tile are cousins, not siblings, so there is no combinator and no
       * `:has()` that reaches from one to the other — the same reason the
       * `change` handler above exists.
       *
       * `focusin`/`focusout` rather than `focus`/`blur` because those do not
       * bubble, and this is delegated on the container so it survives the
       * Section Rendering API replacing the panel.
       */
      const ringFor = (event, on) => {
        const input = event.target.closest?.('input[type="radio"]');
        if (!input) return;
        const tile = this.querySelector(`[data-qv-tile][for="${CSS.escape(input.id)}"]`);
        if (tile) tile.classList.toggle('is-focused', on);
      };
      this.addEventListener('focusin', (event) => ringFor(event, true));
      this.addEventListener('focusout', (event) => ringFor(event, false));
    }

    onClick(event) {
      const trigger = event.target.closest('[data-quick-view]');
      if (!trigger) return;

      // Let the browser do its normal thing for new-tab intents.
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;

      const url = trigger.dataset.productUrl || trigger.getAttribute('href');
      if (!url) return;

      event.preventDefault();
      this.open(url, trigger);
    }

    async open(url, trigger) {
      trigger.setAttribute('aria-busy', 'true');
      this.content.setAttribute('aria-busy', 'true');

      try {
        const separator = url.includes('?') ? '&' : '?';
        // Both in flight at once — the script is not a dependency of the
        // fetch, and serialising them would add its round trip to the time
        // the visitor waits for the panel.
        const [response] = await Promise.all([
          fetch(`${url}${separator}section_id=${SECTION_ID}`),
          loadProductInfo(this.dataset.productInfoSrc),
        ]);
        if (!response.ok) throw new Error(`${response.status}`);
        const html = await response.text();

        // The Section Rendering API returns the section wrapped in its own
        // container div. Take the <product-info> out of it so the modal is
        // not nested two divs deeper than its CSS expects.
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const panel = parsed.querySelector('product-info');
        if (!panel) throw new Error('no panel');

        this.content.innerHTML = '';
        this.content.appendChild(panel);

        // Shopify's dynamic checkout button is injected by their script and
        // has to be re-initialised for markup that arrived after page load.
        // Without this the BUY IT NOW button renders as an empty box.
        window.Shopify?.PaymentButton?.init?.();

        const dialog = this.closest('modal-dialog');
        dialog?.show(trigger);
      } catch {
        // A failed fetch must not strand the visitor in an empty dialog. Fall
        // back to the destination the link already had.
        window.location.href = url;
      } finally {
        trigger.removeAttribute('aria-busy');
        this.content.setAttribute('aria-busy', 'false');
      }
    }
  }

  /**
   * THE PHOTOGRAPH IS THE ONE THING DAWN CANNOT SWITCH HERE.
   * ---------------------------------------------------------------------------
   * `product-info.js` changes media through `updateMedia`, which drives a
   * `<media-gallery>`. Quick view deliberately has none — docs/12 §11 omits the
   * thumbnail rail, and §15.8 says keeping the surface reduced is the right
   * call. So on a variant change everything else updated and the picture stayed
   * on the colour the panel opened with, which is the most misleading half of
   * the bug: the customer is looking at the photograph, not at the hidden input.
   *
   * `variantChange` already carries the re-rendered section as `data.html`, so
   * the replacement is a lift from the document we have already been handed —
   * no second fetch, and the markup is whatever `quick-view.liquid` renders
   * today rather than a copy of it maintained here.
   *
   * `--qv-backdrop` comes across with it because it is an inline style on the
   * same element, so the blurred ground follows the colourway too.
   *
   * Guarded on `sectionId` because this subscription is global and the PDP
   * publishes the same event for its own section.
   */
  subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
    if (event?.data?.sectionId !== SECTION_ID) return;

    const source = event.data.html?.getElementById(`QuickViewMedia-${SECTION_ID}`);
    const destination = document.getElementById(`QuickViewMedia-${SECTION_ID}`);
    if (!source || !destination) return;

    destination.innerHTML = source.innerHTML;
    const backdrop = source.style.getPropertyValue('--qv-backdrop');
    if (backdrop) destination.style.setProperty('--qv-backdrop', backdrop);
  });

  if (!customElements.get('quick-view')) {
    customElements.define('quick-view', QuickView);
  }
})();
